import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, deleteDoc, doc, getDocs, writeBatch, addDoc, limit, updateDoc } from 'firebase/firestore';
import SearchAndFilterBar from './SearchAndFilterBar';
import MessageModal from './MessageModal';
import SellItemModal from './SellItemModal';
import { usePagination } from '../hooks/usePagination';
import PaginationControls from './PaginationControls';
import ExportOptionsModal from './ExportOptionsModal';
import { reportFields } from '../config/reportFields';
import papaparse from 'papaparse';

const getProductStatus = (profitPercentage) => {
  if (profitPercentage === null || isNaN(profitPercentage)) return 'N/A';
  if (profitPercentage >= 80) return 'Profit';
  if (profitPercentage >= 50) return 'Average';
  if (profitPercentage >= 30) return 'Poor';
  if (profitPercentage >= 1) return 'Bad';
  return 'Loss';
};

const ChildItemsList = ({ parentDocId, onEditItem, onSellItem, onDeleteItem }) => {
    const [childItems, setChildItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const q = query(collection(db, "inventoryItems"), where("parentDocId", "==", parentDocId), orderBy("childId"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(d => d.data());
            setChildItems(items);
            setLoading(false);
        }, (err) => {
            console.error("Error fetching child items: ", err);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [parentDocId]);

    if (loading) return <tr><td colSpan="7" style={{ textAlign: 'center', padding: '1rem' }}>Loading items...</td></tr>;
    if (childItems.length === 0) return <tr><td colSpan="7" style={{ textAlign: 'center', padding: '1rem' }}>No individual items found for this batch.</td></tr>;

    return (
        <>
            <tr className="child-item-row-header">
                <td>Item Image</td><td>Child ID</td><td>Description</td>
                <td className="cell-center">Selling Price (RM)</td><td className="cell-center">Profit (RM)</td>
                <td className="cell-center">Status</td><td className="cell-actions">Actions</td>
            </tr>
            {childItems.map(item => (
                <tr key={item.childId} className={`child-item-row ${item.status === 'Sold' ? 'sold' : ''}`}>
                    <td><img src={item.imageUrl} alt={item.itemName} className="table-image-preview" /></td>
                    <td><button onClick={() => onEditItem(item.childId)} className="product-id-link">{item.childId}</button></td>
                    <td>{item.itemDescription || item.itemName}</td>
                    <td className="cell-center">{Number(item.sellingPriceMYR || 0).toFixed(2)}</td>
                    <td className="cell-center" style={{ color: (item.profitMYR || 0) < 0 ? 'var(--brand-danger)' : 'var(--brand-success)' }}>{Number(item.profitMYR || 0).toFixed(2)}</td>
                    <td className="cell-center">{item.status}</td>
                    <td className="cell-actions">
                        <button className="sell-button" disabled={item.status === 'Sold'} onClick={() => onSellItem(item)}>Sell</button>
                        <button className="delete-button" onClick={() => onDeleteItem(item.childId)}>Delete</button>
                    </td>
                </tr>
            ))}
        </>
    );
};

const ProductList = ({ user, onEdit, onView, onEditItem, onAddNewChild, categoryFilter }) => {
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [confirmation, setConfirmation] = useState({ message: '', onConfirm: null, showCancel: false });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ status: { label: 'Status', value: 'all', options: ['Profit', 'Average', 'Poor', 'Bad', 'Loss'] }});
  const [isSellModalOpen, setIsSellModalOpen] = useState(false);
  const [itemToSell, setItemToSell] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const paginationProps = usePagination(filteredProducts, 25);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    setLoading(true);

    const productQuery = query(collection(db, "products"), where("userId", "==", user.uid), orderBy("createdAt", "desc"));
    const unsubscribeProducts = onSnapshot(productQuery, (snapshot) => {
        setAllProducts(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
        setLoading(false);
    });

    const invQuery = query(collection(db, "inventoryItems"), where("userId", "==", user.uid));
    const unsubscribeInventory = onSnapshot(invQuery, (snap) => {
        setInventoryItems(snap.docs.map(d => d.data()));
    });
    
    const salesQuery = query(collection(db, "sales"), where("founderId", "==", user.uid));
    const unsubscribeSales = onSnapshot(salesQuery, (snap) => {
        setSalesData(snap.docs.map(d => ({...d.data(), saleDate: d.data().saleDate.toDate() })));
    });

    return () => {
        unsubscribeProducts();
        unsubscribeInventory();
        unsubscribeSales();
    };
  }, [user]);

  useEffect(() => {
    let results = [...allProducts];
    if (categoryFilter) results = results.filter(p => p.category === categoryFilter);
    if (filters.status.value !== 'all') results = results.filter(p => getProductStatus(p.profitPercentage) === filters.status.value);
    if (searchTerm) {
        results = results.filter(p => 
            (p.itemName && p.itemName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.productId && p.productId.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }
    setFilteredProducts(results);
  }, [searchTerm, filters, categoryFilter, allProducts]);

  const closeConfirmationModal = () => setConfirmation({ message: '', onConfirm: null, showCancel: false });

  const handleAddNewChildClick = async (parentProduct) => {
    const prefixMatch = parentProduct.productId.match(/^([a-zA-Z0-9]+?)(\d+)$/);
    if (!prefixMatch) {
      alert("Could not determine prefix from parent Product ID.");
      return;
    }
    const prefix = prefixMatch[1];
    const numericPartOfParent = parseInt(parentProduct.productId.replace(prefix, ''), 10);
    const childIdPrefix = `${prefix}${numericPartOfParent}`;
    const childrenQuery = query(collection(db, "inventoryItems"), where("parentDocId", "==", parentProduct.id), orderBy("childId", "desc"), limit(1));
    const childrenSnapshot = await getDocs(childrenQuery);
    let nextChildNumber = 1;
    if (!childrenSnapshot.empty) {
        const lastChildId = childrenSnapshot.docs[0].id;
        const lastChildNumPart = lastChildId.replace(childIdPrefix, '');
        nextChildNumber = parseInt(lastChildNumPart, 10) + 1;
    }
    const newChildId = `${childIdPrefix}${String(nextChildNumber).padStart(3, '0')}`;
    onAddNewChild(parentProduct, newChildId);
  };

  const handleDeleteParentProduct = (product) => {
    if (user.role !== 'super-admin') {
      setConfirmation({ message: "You do not have permission to perform this action.", showCancel: false, onConfirm: closeConfirmationModal });
      return;
    }
    setConfirmation({
      message: `Are you sure you want to delete the entire batch '${product.itemName}' (${product.productId})? This will also delete all of its individual items and cannot be undone.`,
      showCancel: true,
      onConfirm: async () => {
        const batch = writeBatch(db);
        const childrenQuery = query(collection(db, "inventoryItems"), where("parentDocId", "==", product.id));
        const childrenSnapshot = await getDocs(childrenQuery);
        childrenSnapshot.forEach(doc => { batch.delete(doc.ref); });
        const parentRef = doc(db, "products", product.id);
        batch.delete(parentRef);
        await batch.commit();
        closeConfirmationModal();
      },
      onCancel: closeConfirmationModal
    });
  };

  const handleDeleteItem = (itemId) => {
      if (user.role !== 'super-admin') {
          setConfirmation({ message: "You do not have permission to delete items.", showCancel: false, onConfirm: closeConfirmationModal });
          return;
      }
      setConfirmation({
          message: `Are you sure you want to delete item ${itemId}? This action cannot be undone.`,
          showCancel: true,
          onConfirm: async () => {
              await deleteDoc(doc(db, "inventoryItems", itemId));
              closeConfirmationModal();
          },
          onCancel: closeConfirmationModal
      });
  };

  const handleSellItem = (item) => {
      if (item.status === 'Sold') return;
      setItemToSell(item);
      setIsSellModalOpen(true);
  };

  const handleConfirmSale = async (item, finalPrice, finalProfit, saleDate) => {
      await updateDoc(doc(db, "inventoryItems", item.childId), { 
          status: 'Sold', sellingPriceMYR: finalPrice, profitMYR: finalProfit,
          profitPercentage: item.perUnitCostMYR > 0 ? (finalProfit / item.perUnitCostMYR) * 100 : 0,
      });
      await addDoc(collection(db, "sales"), {
          productId: item.parentDocId, productName: item.itemName, productIdString: item.parentReadableId,
          childId: item.childId, founderId: user.uid, founderName: user.displayName,
          quantitySold: 1, profitPerItem: finalProfit, totalProfit: finalProfit, saleDate: saleDate 
      });
      setIsSellModalOpen(false);
      setItemToSell(null);

      const profitPercentage = item.perUnitCostMYR > 0 ? (finalProfit / item.perUnitCostMYR) * 100 : 0;
      let successMsg = "Sale recorded! That's a good Sale. Keep up the good work!";
      if (profitPercentage < 30) successMsg = "Sale recorded! That's not a good sale. You need to analyse more.";
      else if (profitPercentage < 50) successMsg = "Sale recorded! Not bad. You can do better!";
      setConfirmation({ message: successMsg, onConfirm: closeConfirmationModal, showCancel: false });
  };
  
  const prepareProductOverviewData = () => {
    const parentMap = new Map(allProducts.map(p => [p.id, p]));
    const salesDateMap = new Map(salesData.map(sale => [sale.childId, sale.saleDate]));

    return inventoryItems.map(child => {
        const parent = parentMap.get(child.parentDocId);
        if (!parent) return null;
        
        const soldDate = salesDateMap.get(child.childId);

        return {
            productId: parent.productId, parentItemName: parent.itemName, parentCategory: parent.category,
            parentTotalItems: parent.totalItems, parentPerUnitCostMYR: parent.perUnitCostMYR,
            childId: child.childId, childItemName: child.itemName, childStatus: child.status,
            soldDate: soldDate ? soldDate.toLocaleDateString('en-GB') : '',
            childSize: child.size, childColor: child.color, childPerUnitCostMYR: child.perUnitCostMYR,
            childSellingPriceMYR: child.sellingPriceMYR, childProfitMYR: child.profitMYR
        };
    }).filter(Boolean);
  };

  const handleExportClick = () => {
    if (inventoryItems.length === 0) {
        alert("No data to export.");
        return;
    }
    setIsExportModalOpen(true);
  };
  
  const handleConfirmExport = (selectedKeys) => {
    const preparedData = prepareProductOverviewData();
    const headerMap = Object.fromEntries(reportFields.product_overview.map(f => [f.key, f.label]));
    const dataToExport = preparedData.map(row => {
        const newRow = {};
        selectedKeys.forEach(key => { newRow[key] = row[key]; });
        return newRow;
    });
    const csv = papaparse.unparse(dataToExport, { header: true, columns: selectedKeys, transformHeader: (h) => headerMap[h] || h });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `product_overview_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsExportModalOpen(false);
  };

  const toggleRow = (productId) => setExpandedRows(prev => ({...prev, [productId]: !prev[productId]}));

  if (loading) return <p>Loading product list...</p>;
  if (error) return <p style={{ color: 'var(--brand-danger)' }}>{error}</p>;

  return (
    <div className="product-list-container">
      <ExportOptionsModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          onConfirmExport={handleConfirmExport}
          availableFields={reportFields.product_overview || []}
      />
      {isSellModalOpen && (
          <SellItemModal 
              item={itemToSell}
              user={user}
              onClose={() => setIsSellModalOpen(false)}
              onConfirmSale={handleConfirmSale}
          />
      )}
      <MessageModal {...confirmation} onCancel={closeConfirmationModal} />
      <div className="page-header">
        <h3>{categoryFilter ? `${categoryFilter}` : "Product Overview"}</h3>
        <div style={{display: 'flex', gap: '1rem', alignItems: 'center'}}>
            <SearchAndFilterBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} filters={filters} setFilters={setFilters} />
            <button className="submit-button" onClick={handleExportClick}>Export</button>
        </div>
      </div>
      <div className="table-wrapper">
        <table className="product-list-table">
          <thead>
            <tr>
              <th></th><th>Image</th><th>Batch ID</th><th>Batch Name</th>
              <th className="cell-center">Total Items</th><th className="cell-center">Avg. Unit Cost (RM)</th>
              <th className="cell-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginationProps.paginatedData.map(product => (
              <React.Fragment key={product.id}>
                <tr className="parent-row">
                  <td><button onClick={() => toggleRow(product.id)} className="product-id-link expand-arrow">{expandedRows[product.id] ? '▼' : '►'}</button></td>
                  <td><img src={product.imageUrl} alt={product.itemName} className="table-image-preview" /></td>
                  <td><button onClick={() => onView(product.id)} className="product-id-link">{product.productId}</button></td>
                  <td>{product.itemName}</td>
                  <td style={{ textAlign: 'center' }}>{product.totalItems}</td>
                  <td className="cell-center">{Number(product.perUnitCostMYR).toFixed(2)}</td>
                  <td className="cell-actions">
                      <button className="edit-button" onClick={() => onEdit(product.id)}>Edit Batch</button>
                      <button className="delete-button" onClick={() => handleDeleteParentProduct(product)}>Delete Batch</button>
                      <button className="add-another-button" title="Add New Item to this Batch" onClick={() => handleAddNewChildClick(product)}>+</button>
                  </td>
                </tr>
                {expandedRows[product.id] && (
                  <ChildItemsList 
                      parentDocId={product.id}
                      onEditItem={onEditItem} 
                      onSellItem={handleSellItem} 
                      onDeleteItem={handleDeleteItem} 
                  />
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationControls paginationProps={paginationProps} />
    </div>
  );
};

export default ProductList;